import { useState, useEffect, useCallback } from "react";
import { AuthorizedCreditReportResultDTO } from "../types/AuthorizedCreditReportResultDTO";
import CreditAuthorizationsApiClient from "../api/creditAuthorizationsApiClient";
import { useAuthenticatedApiClient } from "./useAuthenticatedApiClient";

interface AuthorizedCreditReportResultFlyoutResult {
  creditReportResult: AuthorizedCreditReportResultDTO | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  downloadPdf: () => Promise<void>;
}

export const useAuthorizedCreditReportResultFlyout = (
  creditAuthorizationId?: string | null
): AuthorizedCreditReportResultFlyoutResult => {
  const [creditReportResult, setCreditReportResult] = useState<AuthorizedCreditReportResultDTO | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const creditAuthorizationsApiClient = useAuthenticatedApiClient(CreditAuthorizationsApiClient);

  const fetchCreditReportResult = useCallback(async () => {
    if (!creditAuthorizationId) {
      setCreditReportResult(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await creditAuthorizationsApiClient.getCreditAuthorizationResult(creditAuthorizationId);
      setCreditReportResult(response);
    } catch (err) {
      console.error("Failed to fetch credit report result:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch credit report result");
      setCreditReportResult(null);
    } finally {
      setLoading(false);
    }
  }, [creditAuthorizationId, creditAuthorizationsApiClient]);

  const refetch = useCallback(() => {
    fetchCreditReportResult();
  }, [fetchCreditReportResult]);

  const downloadPdf = useCallback(async () => {
    if (!creditAuthorizationId) {
      console.error("No credit authorization ID provided");
      return;
    }

    try {
      const response = await creditAuthorizationsApiClient.downloadCreditReportPdf(creditAuthorizationId);
      
      // Create a blob from the response
      const blob = new Blob([response.blobPart as BlobPart], { type: 'application/pdf' });
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.fileName || `credit-report-${creditAuthorizationId}.pdf`;
      
      // Trigger the download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download credit report PDF:", err);
    }
  }, [creditAuthorizationId, creditAuthorizationsApiClient]);

  // Fetch when creditAuthorizationId changes
  useEffect(() => {
    fetchCreditReportResult();
  }, [fetchCreditReportResult]);

  return {
    creditReportResult,
    loading,
    error,
    refetch,
    downloadPdf,
  };
};
